import { createSelector } from '@reduxjs/toolkit'

import {
  selectAccountById,
  selectAllAccounts,
  selectAllRelationships,
  selectAllUsers,
  selectCurrentUser,
  selectUserById,
  type Account,
  type CurrentUser,
  type User,
} from '@/modules/accounts'
import type { RootState } from '@/store/root-reducer'

import { buildAccessIndex, canViewProject, type AccessIndex } from './access'
import { eventsAdapter, projectsAdapter, projectUsersAdapter } from './store'
import type { ProjectEvent, ProjectRecord, ProjectUser } from './types'

const projectSelectors = projectsAdapter.getSelectors((state: RootState) => state.projects.projects)
const projectUserSelectors = projectUsersAdapter.getSelectors((state: RootState) => state.projects.projectUsers)
const eventSelectors = eventsAdapter.getSelectors((state: RootState) => state.projects.events)

export const selectProjectRecordById = projectSelectors.selectById
export const selectAllProjectRecords = projectSelectors.selectAll
export const selectAllProjectUsers = projectUserSelectors.selectAll

const arg = <T>(_state: RootState, value: T) => value

/** Lookups for the access rules. Rebuilt only when accounts, links, assignments or projects change. */
export const selectAccessIndex = createSelector(
  [selectAllAccounts, selectAllRelationships, selectAllProjectUsers, selectAllProjectRecords],
  (accounts, relationships, projectUsers, projects): AccessIndex => buildAccessIndex(accounts, relationships, projectUsers, projects),
)

/** The projects the signed-in user may open. The ONE place a list of projects is scoped; screens never filter by role. */
export const selectVisibleProjectRecords = createSelector(
  [selectAllProjectRecords, selectCurrentUser, selectAccessIndex],
  (projects, user, ix): ProjectRecord[] => (user ? projects.filter((project) => canViewProject(ix, user, project)) : []),
)

export type ProjectAccess =
  | { status: 'ok'; project: ProjectRecord }
  | { status: 'missing' }
  | { status: 'forbidden' }

// Stable results, so `useSelector` sees "no change" and screens don't re-render on unrelated updates.
const MISSING: ProjectAccess = { status: 'missing' }
const FORBIDDEN: ProjectAccess = { status: 'forbidden' }
const okResults = new WeakMap<ProjectRecord, ProjectAccess>()

/**
 * Whether the signed-in user may open this project. Used by the project route so a typed or bookmarked URL for a
 * project outside the user's scope is refused, not just hidden from the list.
 */
export function selectProjectAccess(state: RootState, projectId: string): ProjectAccess {
  const project = selectProjectRecordById(state, projectId)
  const user = selectCurrentUser(state)
  if (!project) return MISSING
  if (!user || !canViewProject(selectAccessIndex(state), user, project)) return FORBIDDEN
  let result = okResults.get(project)
  if (!result) okResults.set(project, (result = { status: 'ok', project }))
  return result
}

export const selectSupplierOfProject = (state: RootState, project: Pick<ProjectRecord, 'clientAccountId'>): Account | undefined => {
  const supplierId = selectAccessIndex(state).supplierOfClient.get(project.clientAccountId)
  return supplierId ? selectAccountById(state, supplierId) : undefined
}

export const selectClientOfProject = (state: RootState, project: Pick<ProjectRecord, 'clientAccountId'>) =>
  selectAccountById(state, project.clientAccountId)

/** History of a project, newest first. */
export const selectEventsOfProject = createSelector([eventSelectors.selectAll, arg<string>], (events, projectId): ProjectEvent[] =>
  events.filter((event) => event.projectId === projectId).toSorted((a, b) => b.createdAt.localeCompare(a.createdAt)),
)

export interface Participant {
  assignment: ProjectUser
  user: User
  account: Account | undefined
}

/** People explicitly assigned to a project (ProjectUsers), with their account. */
export const selectParticipantsOfProject = createSelector(
  [selectAllProjectUsers, selectAllUsers, selectAllAccounts, arg<string>],
  (assignments, users, accounts, projectId): Participant[] => {
    const byId = new Map(users.map((user) => [user.id, user]))
    const accountById = new Map(accounts.map((account) => [account.id, account]))
    return assignments
      .filter((item) => item.projectId === projectId)
      .flatMap((assignment) => {
        const user = byId.get(assignment.userId)
        return user ? [{ assignment, user, account: accountById.get(user.accountId) }] : []
      })
      .toSorted((a, b) => a.assignment.assignedAt.localeCompare(b.assignment.assignedAt))
  },
)

export const selectUserName = (state: RootState, userId: string) => {
  const user = selectUserById(state, userId)
  return user ? `${user.firstName} ${user.lastName}` : 'Someone'
}

export type { CurrentUser }
